import {
  deserify,
  fetchImageAsBase64,
  getMediaBasePath,
  getTeacherSubjectName,
} from '@geneo2-web/shared-ui';
import { Box } from '@mui/material';
import {
  ContentElementType,
  ImageOption,
  ImageType,
  McqMultipleContentModel,
  McqSingleContentModel,
  OptionsType,
  Question,
  SolutionElementType,
} from '@protos/content_management/content.db_pb';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import html2pdf from 'html2pdf.js';
import React, { CSSProperties, forwardRef, useRef, useState } from 'react';
import { setToastInfo } from '../../../../Home/reducer/homeDashboard.slice';
import { isGifMp4 } from 'libs/shared-ui/src/components/elements/ImageWrapper';
import { QPGPreviewHtml } from './QPGPreviewHtml';
import { default as Geneologo } from 'libs/shared-ui/src/assets/images/geneo-logo.png';
import { getSubjectNameFromEnum } from '../../../../Home/home.utils';

const printableSx: CSSProperties = {
  breakInside: 'avoid',
  pageBreakInside: 'avoid',
};

interface IPropsQPGPrintablePaper {
  printAnswer?: boolean;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  printableFontSize?: number;
  SchoolLogo?: string;
}

const QPGPrintablePaper = forwardRef<any, IPropsQPGPrintablePaper>(
  ({ setLoading, printableFontSize, printAnswer, SchoolLogo }, ref) => {
    const printableRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch?.() || (() => {});
    const [showData, setShowData] = useState(false);

    const { qpg_content } = deserify(
      useAppSelector((state) => state.assessment)
    );
    const { user_info } = deserify(useAppSelector((state) => state.auth));

    // ----------------------------------------------------

    const sanitizeFilenamePart = (part: string) => {
      return part
        .replace(/[^a-zA-Z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
    };

    // ----------------------------------------------------

    const getSubjectsClassFilenamePrefix = () => {
      const sections = qpg_content?.multiSubjectSections ?? [];
      const subjectNames = [
        ...new Set(
          sections
            .map((section) => {
              const name =
                getTeacherSubjectName(
                  section.subjectId,
                  user_info?.teachClassSubjects
                ) || getSubjectNameFromEnum(section.subject);
              return name?.trim();
            })
            .filter((name): name is string => !!name)
        ),
      ];
      const classPart = qpg_content?.assessment?.class
        ? String(qpg_content.assessment.class)
        : '';
      const parts = [...subjectNames];
      if (classPart) parts.push('Class_' + classPart);
      return parts.length ? `${parts.join('_')}_` : '';
    };

    // ----------------------------------------------------

    const getLogoDimensions = (
      imgWidth: number,
      imgHeight: number,
      targetHeight: number
    ) => {
      const ratio = targetHeight / imgHeight;
      return {
        width: imgWidth * ratio,
        height: targetHeight,
      };
    };

    // ----------------------------------------------------

    const loadImage = (src: string) =>
      new Promise<HTMLImageElement>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.src = src;
      });

    // ----------------------------------------------------

    const questionsList = qpg_content?.assessmentContent ?? [];

    const processElements = async (
      arr: ContentElementType[] | SolutionElementType[]
    ) => {
      if (!Array.isArray(arr)) return arr;
      return Promise.all(
        arr.map(async (element) => {
          if (element?.model?.value instanceof ImageType) {
            const url = element.model.value.imageUrl;
            if (url && !url.startsWith('data:')) {
              let fullUrl = getMediaBasePath(url, 'processedMediaBucket');
              fullUrl = isGifMp4(fullUrl)
                ? fullUrl.replace('mp4', 'jpg')
                : fullUrl;
              const base64 = await fetchImageAsBase64(
                getMediaBasePath(fullUrl, 'processedMediaBucket')
              );
              if (base64) {
                element.model.value.imageUrl = base64;
              }
            }
          }
          return element;
        })
      );
    };

    // ----------------------------------------------------

    const processOptions = async (arr: OptionsType[]) => {
      if (!Array.isArray(arr)) return arr;
      return Promise.all(
        arr.map(async (option) => {
          if (
            option?.optionType?.value instanceof ImageOption &&
            option.optionType.value.image instanceof ImageType
          ) {
            const url = option.optionType.value.image.imageUrl;
            if (url && !url.startsWith('data:')) {
              let fullUrl = getMediaBasePath(url, 'processedMediaBucket');
              fullUrl = isGifMp4(fullUrl)
                ? fullUrl.replace('mp4', 'jpg')
                : fullUrl;
              const base64 = await fetchImageAsBase64(
                getMediaBasePath(fullUrl, 'processedMediaBucket')
              );
              if (base64) {
                option.optionType.value.image.imageUrl = base64;
              }
            }
          }
          return option;
        })
      );
    };

    // ----------------------------------------------------

    React.useImperativeHandle(ref, () => ({
      handlePrint: async (isAnswer = false, title = '') => {
        try {
          if (setLoading) setLoading(true);

          // --- Step 1: Preprocess Questions before Rendering ---
          const processedQuestions: Question[] = [];
          if (questionsList) {
            for (let i = 0; i < questionsList.length; i++) {
              const question = new Question(questionsList[i]);

              if (question.question?.model?.value?.commonQuestionContent) {
                // Elements
                question.question.model.value.commonQuestionContent.elements =
                  await processElements(
                    question.question.model.value.commonQuestionContent.elements
                  );

                // Solutions
                question.question.model.value.commonQuestionContent.solution =
                  await processElements(
                    question.question.model.value.commonQuestionContent.solution
                  );

                // Options (for MCQ)
                if (
                  question.question.model.value instanceof
                    McqSingleContentModel ||
                  question.question.model.value instanceof
                    McqMultipleContentModel
                ) {
                  question.question.model.value.options = await processOptions(
                    question.question.model.value.options
                  );
                }
              }

              processedQuestions.push(question);
            }
          }

          // --- Step 2: Render DOM with Processed Data ---
          // setFetchedQuestions(processedQuestions);
          setShowData(true);

          // Allow React render & paint to complete
          await new Promise((r) => setTimeout(r, 300));

          const el = printableRef.current;
          if (!el) throw new Error('Printable element not found');

          // --- Step 3: Wait for all images to load ---
          const waitForImagesToLoad = async (container: HTMLElement) => {
            const imgs = Array.from(container.querySelectorAll('img'));
            await Promise.all(
              imgs.map(
                (img) =>
                  new Promise<void>((resolve) => {
                    if (img.complete) return resolve();
                    img.onload = img.onerror = () => resolve();
                  })
              )
            );
          };

          await waitForImagesToLoad(el);

          // --- Step 4: Wait for DOM layout stability ---
          await new Promise((r) => requestAnimationFrame(r));

          // --- Step 5: Generate PDF ---
          const assessmentTitle = sanitizeFilenamePart(
            title || qpg_content?.assessment?.assessmentTitle || 'Assessment'
          );
          const opt = {
            margin: [12, 12, 25, 12],
            filename: `${getSubjectsClassFilenamePrefix()}${assessmentTitle}${
              isAnswer ? '_Answers' : '_Questions'
            }.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
            },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: {
              mode: ['css', 'legacy', 'whiteline'],
              avoid: ['.avoid-break-element', 'img'],
            },
          };

          const pdf = await html2pdf().set(opt).from(el).toPdf().get('pdf');
          const totalPages = pdf.internal.getNumberOfPages();
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const footerY = pageHeight - 8;
          const lineY = footerY - 8;

          // --- Logos ---

          const rightLogo = Geneologo;
          const leftLogo = SchoolLogo
            ? await fetchImageAsBase64(SchoolLogo)
            : undefined;
          const logoY = 5;

          // --- Step 5: Add header logos + footer ---
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);

            // LEFT LOGO
            if (i === 1) {
              if (leftLogo) {
                const leftLogoHeight = 15; // Desired logo height in mm
                const leftImg = await loadImage(leftLogo);
                const leftLogoSize = getLogoDimensions(
                  leftImg.width,
                  leftImg.height,
                  leftLogoHeight
                );
                pdf.addImage(
                  leftLogo,
                  'PNG',
                  10,
                  logoY,
                  leftLogoSize.width,
                  leftLogoSize.height
                );

                const rightLogoHeight = 10; // Desired logo height in mm
                // RIGHT LOGO
                const rightImg = await loadImage(rightLogo);
                const rightLogoSize = getLogoDimensions(
                  rightImg.width,
                  rightImg.height,
                  rightLogoHeight
                );
                pdf.addImage(
                  rightLogo,
                  'PNG',
                  pageWidth - rightLogoSize.width - 10,
                  5,
                  rightLogoSize.width,
                  rightLogoSize.height
                );
              }
            }

            // Footer line
            pdf.setFontSize(10);
            pdf.setDrawColor(150);
            pdf.setLineWidth(0.2);
            pdf.line(10, lineY, pageWidth - 10, lineY);

            if (i === 1) {
              pdf.text('© Copyright reserved.', 10, footerY, { align: 'left' });

              pdf.text(
                `This Paper consists of ${totalPages} printed pages.`,
                pageWidth / 2,
                footerY,
                { align: 'center' }
              );
            } else {
              pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, footerY, {
                align: 'center',
              });
            }
          }

          await pdf.save(opt.filename);

          // --- Step 6: Cleanup ---
          setShowData(false);
          if (setLoading) setLoading(false);
        } catch (error) {
          console.error('Error generating PDF:', error);

          dispatch(
            setToastInfo({
              label: 'Error while generating pdf',
              variant: 'error',
              open: true,
            })
          );

          if (setLoading) setLoading(false);
        }
      },
    }));

    return (
      <Box sx={{ display: 'none' }}>
        {showData && (
          <Box
            ref={printableRef}
            sx={{
              fontSize: printableFontSize
                ? printableFontSize + 'px !important'
                : undefined,
            }}
          >
            <QPGPreviewHtml printAnswer={printAnswer} />
          </Box>
        )}
      </Box>
    );
  }
);

export default QPGPrintablePaper;
